import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Users, Zap, ExternalLink } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { tradeDealsAPI } from '../services/api';

const TradeDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchDeals = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await tradeDealsAPI.getAll(params);
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching trade deals:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-container border-t-transparent dark:border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title="Trade Deals & Agreements | Bilateral & Multilateral Trade Pacts"
        description="Track bilateral and multilateral trade deals, free trade agreements, and AI-extracted trade pacts worldwide. Monitor active, pending, and expired trade agreements with impact scores."
        path="/trade-deals"
        keywords="trade deals, trade agreements, bilateral trade, multilateral trade, free trade agreement, trade pact, international trade deals, trade negotiations"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface">Trade Deals</h1>
        <p className="mt-2 text-slate-600 dark:text-on-surface-variant">Bilateral and multilateral trade agreements</p>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <div className="flex space-x-2">
          {['all', 'Active', 'Pending', 'Expired', 'AI-Extracted'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`rounded-stitch-lg px-4 py-2 font-medium transition-colors font-headline ${
                filter === status
                  ? 'bg-primary-container text-white'
                  : 'border border-slate-200/80 bg-white text-slate-700 hover:bg-slate-50 dark:border-outline-variant/15 dark:bg-surface-container-low dark:text-on-surface-variant dark:hover:bg-surface-container-high'
              }`}
            >
              {status === 'AI-Extracted' ? '🤖 AI-Extracted' : status}
            </button>
          ))}
        </div>
      </div>

      {/* Deals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {deals.map((deal) => (
          <div key={deal.id} className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                {deal.data_source === 'ai_extracted' ? (
                  <Zap className="h-6 w-6 text-yellow-500 mr-3" />
                ) : (
                  <FileText className="h-6 w-6 text-primary-600 dark:text-primary mr-3" />
                )}
                <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface line-clamp-2">{deal.name}</h3>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                deal.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                deal.status === 'Pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                deal.status === 'AI-Extracted' ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' :
                'bg-slate-100 text-slate-800 dark:bg-secondary-container dark:text-on-secondary-container'
              }`}>
                {deal.data_source === 'ai_extracted' ? '🤖 AI' : deal.status}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
                <Users className="h-4 w-4 mr-2" />
                Type: {deal.type}
              </div>
              {deal.sector && (
                <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
                  <FileText className="h-4 w-4 mr-2" />
                  Sector: {deal.sector}
                </div>
              )}
              {deal.effective_date && (
                <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
                  <Calendar className="h-4 w-4 mr-2" />
                  Effective: {new Date(deal.effective_date).toLocaleDateString()}
                </div>
              )}
              {deal.signed_date && !deal.data_source && (
                <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
                  <Calendar className="h-4 w-4 mr-2" />
                  Signed: {new Date(deal.signed_date).toLocaleDateString()}
                </div>
              )}
              {deal.impact_score != null && (
                <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    deal.impact_score >= 60 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                    deal.impact_score >= 40 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  }`}>
                    Impact: {deal.impact_score}/100
                  </span>
                  {deal.confidence_score != null && (
                    <span className="ml-2 text-xs text-slate-400 dark:text-outline">
                      Confidence: {(parseFloat(deal.confidence_score) * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              )}
            </div>

            {deal.description && (
              <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-4 line-clamp-3">{deal.description}</p>
            )}

            {deal.participant_countries && deal.participant_countries.filter(Boolean).length > 0 && (
              <div className="border-t border-slate-100 pt-4 dark:border-outline-variant/10">
                <p className="text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">Participants:</p>
                <div className="flex flex-wrap gap-2">
                  {deal.participant_countries.filter(Boolean).map((country, idx) => (
                    <span
                      key={idx}
                      className="rounded-stitch bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-secondary-container dark:text-on-secondary-container"
                    >
                      {country}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {deal.source_url && (
              <div className="mt-3 border-t border-slate-100 pt-3 dark:border-outline-variant/10">
                <a href={deal.source_url} target="_blank" rel="noopener noreferrer"
                   className="text-sm text-primary-600 dark:text-primary hover:underline flex items-center gap-1">
                  View Source <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      {deals.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-on-surface-variant">No trade deals found.</p>
          <p className="mt-2 text-sm text-slate-400 dark:text-outline">
            Run the pipeline to extract trade deals from news:{' '}
            <code className="rounded-stitch bg-slate-100 px-2 py-0.5 dark:bg-surface-container-high">
              cd server && npm run pipeline
            </code>
          </p>
        </div>
      )}
    </div>
  );
};

export default TradeDeals;
